import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useReveal } from "@/lib/useReveal";
import { Check, Clock, Users } from "lucide-react";

export default function Tasting({ onReserve }) {
    const [packages, setPackages] = useState([]);
    const { ref, visible } = useReveal();

    useEffect(() => {
        api.get("/tastings").then((r) => setPackages(r.data)).catch(() => setPackages([]));
    }, []);

    return (
        <section
            id="degustace"
            ref={ref}
            data-testid="tasting-section"
            className="py-24 md:py-36 px-6 md:px-12 bg-cream relative overflow-hidden"
        >
            {/* Background image right side */}
            <div className="absolute top-0 right-0 w-[44%] h-full hidden lg:block">
                <img
                    src="https://i.ibb.co/DfYfyQXN/IMG-9561.jpg"
                    alt=""
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/70 to-cream/30"></div>
            </div>

            <div className="relative mx-auto max-w-[1500px]">
                <div className={`lg:max-w-[58%] mb-16 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                    <p className="overline mb-6">Degustace · Zážitek · Sklep</p>
                    <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-ink mb-6">
                        Přijďte ochutnat <span className="font-serif-italic text-burgundy">naše vína</span>
                    </h2>
                    <p className="max-w-2xl text-base md:text-lg text-ink2 leading-relaxed">
                        Zveme vás do našeho sklepa, kde si můžete vybrat z několika degustačních
                        programů. Doprovázíme je odborným výkladem a domácím občerstvením.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 border border-ink/10 max-w-5xl">
                    {packages.map((p, i) => (
                        <div
                            key={p.id}
                            data-testid={`tasting-package-${p.id}`}
                            className={`bg-cream p-8 md:p-10 flex flex-col group hover:bg-cream2 transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                            style={{ transitionDelay: `${i * 150}ms` }}
                        >
                            <div className="flex items-baseline justify-between mb-6">
                                <span className="overline">0{i + 1}</span>
                                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-ink2">
                                    <span className="flex items-center gap-1.5"><Clock size={12} /> {p.duration_minutes} min</span>
                                </div>
                            </div>
                            <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight mb-3">{p.name}</h3>
                            <p className="text-sm text-ink2 leading-relaxed mb-8">{p.description}</p>

                            <ul className="space-y-3 mb-10 flex-1">
                                {p.includes.map((it) => (
                                    <li key={it} className="flex items-start gap-3 text-sm text-ink">
                                        <Check size={16} className="text-burgundy mt-0.5 shrink-0" />
                                        <span>{it}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="editorial-line mb-6"></div>

                            <div className="flex items-end justify-between mb-6">
                                <div>
                                    <div className="font-serif text-4xl text-ink leading-none">{p.price_per_person} Kč</div>
                                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-ink2 mt-2">
                                        <Users size={12} /> os. · od {p.min_guests} hostů
                                    </div>
                                </div>
                            </div>

                            <button
                                data-testid={`tasting-reserve-${p.id}`}
                                onClick={() => onReserve(p.name)}
                                className="btn-burgundy w-full"
                            >
                                Rezervovat
                            </button>
                        </div>
                    ))}
                </div>

                {/* Editorial quote */}
                <blockquote className={`mt-24 max-w-3xl transition-all duration-1000 delay-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                    <div className="font-serif-italic text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.15]">
                        <span className="text-burgundy text-6xl leading-none align-top">&ldquo;</span>
                        Víno není jen nápoj — je to vyprávění o krajině, počasí a lidech, kteří mu věnovali svůj čas.
                    </div>
                    <footer className="mt-8 flex items-center gap-4">
                        <div className="w-12 h-px bg-burgundy"></div>
                        <span className="text-xs uppercase tracking-[0.25em] text-ink2">Rodina Štrokay</span>
                    </footer>
                </blockquote>
            </div>
        </section>
    );
}
